"use client"

import * as React from "react"
import { usePathname } from "next/navigation"
import { toast, useSonner, type ToastT } from "sonner"

/** sonner's lifetime for a toast that names none (TOAST_LIFETIME, sonner 2.x). ./sonner sets no other. */
const DEFAULT_LIFETIME_MS = 4000
/**
 * A toast this young at a page change is the confirmation for the change itself —
 * `toast.success("Campaign cloned")` then `router.push(...)`, in ~75 places — and
 * rides along to the next page, where its own clock closes it.
 */
const NAVIGATION_GRACE_MS = 2000
/** How far past its own lifetime a paused toast may stay before it is closed anyway. */
const STUCK_GRACE_MS = 8000
const TICK_MS = 1000

type Clock = { toast: ToastT; seenAt: number; visibleMs: number }

/** sonner never closes a loading toast (a pending promise is one) or an Infinity one; neither does this. */
const closesOnItsOwn = (t: ToastT) => t.type !== "loading" && t.duration !== Infinity

/**
 * STG-751 — bounds how long a toast can stay on screen. Mounted once, beside the
 * root <Toaster /> in app/layout.tsx; renders nothing.
 *
 * sonner pauses EVERY toast's timer while its list is `expanded || interacting`,
 * and each flag is cleared by one event that can go missing: a mouseleave (the
 * hovered toast closed with its x shrinks the list away from a still pointer) or
 * a pointerup back in the list (a right-click sets `interacting`; its pointerup
 * need not return). One miss froze every toast, and the Toaster sits above the
 * router, so the frozen queue rode route after route — "Campaign duplicated"
 * through four pages, three older toasts stacked under it. #445 closed one such
 * latch (the Alt+T hotkey, ./sonner.tsx); this does not depend on which one.
 *
 * Two rules, each by toast id, never a bare `toast.dismiss()`:
 *   - a page change closes every toast older than NAVIGATION_GRACE_MS;
 *   - a toast still up STUCK_GRACE_MS past its own duration is closed, counting
 *     only time the tab is visible, as sonner itself does. A reader hovering a
 *     toast keeps it well past 4 s; nothing keeps it for minutes.
 * Loading toasts are exempt from both: they are waiting on work, not a timer.
 */
export function ToastLifetimeGuard() {
  const pathname = usePathname()
  const { toasts } = useSonner()
  const clocks = React.useRef(new Map<ToastT["id"], Clock>())

  // A toast's clock starts when it can close by itself: at creation, or when a
  // loading toast settles. sonner restarts a toast's timer when it is updated in
  // place (same id — useSonner hands back a new object only for that one), so
  // its clock restarts too.
  React.useEffect(() => {
    const now = Date.now()
    const live = new Set<ToastT["id"]>()
    for (const t of toasts) {
      if (!closesOnItsOwn(t)) continue
      live.add(t.id)
      if (clocks.current.get(t.id)?.toast !== t) {
        clocks.current.set(t.id, { toast: t, seenAt: now, visibleMs: 0 })
      }
    }
    for (const id of [...clocks.current.keys()]) {
      if (!live.has(id)) clocks.current.delete(id)
    }
  }, [toasts])

  const lastPathname = React.useRef(pathname)
  React.useEffect(() => {
    if (lastPathname.current === pathname) return
    lastPathname.current = pathname
    const now = Date.now()
    for (const [id, clock] of [...clocks.current]) {
      if (now - clock.seenAt > NAVIGATION_GRACE_MS) {
        clocks.current.delete(id)
        toast.dismiss(id)
      }
    }
  }, [pathname])

  const anyClocked = toasts.some(closesOnItsOwn)
  React.useEffect(() => {
    if (!anyClocked) return
    let lastTick = Date.now()
    const timer = window.setInterval(() => {
      const now = Date.now()
      // A throttled background tab or a long task can deliver one late tick;
      // it never counts for more than two.
      const elapsed = Math.min(now - lastTick, 2 * TICK_MS)
      lastTick = now
      if (document.hidden) return
      for (const [id, clock] of [...clocks.current]) {
        clock.visibleMs += Math.min(elapsed, now - clock.seenAt)
        const lifetime = clock.toast.duration || DEFAULT_LIFETIME_MS
        if (clock.visibleMs > lifetime + STUCK_GRACE_MS) {
          clocks.current.delete(id)
          toast.dismiss(id)
        }
      }
    }, TICK_MS)
    return () => window.clearInterval(timer)
  }, [anyClocked])

  return null
}
